import { useLazyQuery } from "@apollo/client";
import { useForm } from "react-hook-form";
import { useState } from "react";
import { QUERY_DECISIONS } from "api/queries/decisions";

import CTAButton from "components/CTA/Button";
import FormInputText from "components/Form/Input/Text";
import Pagination, { getPaginationOffset } from "components/Pagination";
import DecisionList from "./DecisionList";

interface FormInputs {
  name: string;
  postcode: string;
}

const ITEMS_PER_PAGE = 10;

export default function DashboardDecisionSearch() {
  const [currentPage, setCurrentPage] = useState(1);

  const [
    getDecisions,
    { data: queryData, loading: queryLoading, error: queryError },
  ] = useLazyQuery(QUERY_DECISIONS, { fetchPolicy: "no-cache" });

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<FormInputs>({
    defaultValues: {
      name: "",
      postcode: "",
    },
  });

  const { name: watchName, postcode: watchPostcode } = watch();

  const onSubmit = (data: FormInputs) => {
    getDecisions({
      variables: {
        name: data.name,
        postcode: data.postcode.replaceAll(/\s/g, ""),
        offset: getPaginationOffset(currentPage, ITEMS_PER_PAGE),
        limit: ITEMS_PER_PAGE,
      },
    });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    getDecisions({
      variables: {
        name: watchName,
        postcode: watchPostcode.replaceAll(/\s/g, ""),
        offset: getPaginationOffset(currentPage, ITEMS_PER_PAGE),
        limit: ITEMS_PER_PAGE,
      },
    });
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Find an Application</h3>
      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6">
        <form onSubmit={handleSubmit(onSubmit)} noValidate autoComplete="off">
          <div className="flex flex-row items-end space-x-4">
            <FormInputText
              id="name"
              label="Applicant 1 name"
              register={register}
            />
            <FormInputText
              id="postcode"
              label="Property postcode"
              register={register}
            />
            <CTAButton label="Search" type="submit" loading={queryLoading} />
            {(errors?.name || errors?.postcode) && (
              <p className="text-sm text-red-800">
                Applicant name and postcode are required
              </p>
            )}
            {queryData?.decisions?.data?.totalCount === 0 && (
              <p className="text-sm text-red-800">No decisions found</p>
            )}
          </div>
          {queryError && (
            <p className="mt-2 text-sm text-red-800">
              Sorry, there was an error searching for decisions. Please try
              again or contact support if the problem persists.
            </p>
          )}
        </form>
      </div>

      {queryData?.decisions?.data?.totalCount > 0 && (
        <div>
          <DecisionList decisions={queryData?.decisions?.data?.results} />
          <Pagination
            data={queryData?.decisions?.data}
            itemsPerPage={ITEMS_PER_PAGE}
            currentPage={currentPage}
            setCurrentPage={handlePageChange}
          />
        </div>
      )}
    </div>
  );
}
